import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Box, Typography } from '@mui/material';

import styles from './GraphVerticalBar.module.scss';

export interface GraphVerticalBarProps {
  customStyle?: string;
  isStatic?: boolean;
  leftPosition: number;
  timeLabel?: string | null;
  graphWidth: number;
}

export const BAR_WIDTH = 0.5;

export const EDGE_THRESHOLD_PX = 26;

const GraphVerticalBar: React.FC<GraphVerticalBarProps> = ({
  customStyle = '',
  isStatic = false,
  leftPosition,
  timeLabel = '',
  graphWidth,
}) => {
  const getPositionClass = useMemo(
    () =>
      (posX: number, width: number): string => {
        // For far left positions (less than threshold from left edge)
        if (posX < EDGE_THRESHOLD_PX) return styles.graphTimeLineTextLeft;
        // For far right positions (less than threshold from right edge)
        if (width - posX < EDGE_THRESHOLD_PX) return styles.graphTimeLineTextRight;
        // For all other positions, center the label
        return styles.graphTimeLineTextCenter;
      },
    [],
  );

  const positionStyles = useMemo(
    () => ({
      left: `${leftPosition - BAR_WIDTH}px`,
    }),
    [leftPosition],
  );

  const textPositionClass = useMemo(
    () => getPositionClass(leftPosition, graphWidth),
    [leftPosition, graphWidth, getPositionClass],
  );

  const boxClassName = useMemo(
    () =>
      classNames({
        [styles.graphTimeLine]: true,
        [customStyle]: !!customStyle,
        [styles.graphTimeLineStatic]: isStatic,
      }),
    [customStyle, isStatic],
  );

  const textClassName = useMemo(
    () => classNames(styles.graphTimeLineText, textPositionClass, 'sl-timeline-text'),
    [textPositionClass],
  );

  return (
    <Box className={boxClassName} data-testid="graph-vertical-bar" style={positionStyles}>
      {timeLabel && (
        <Typography
          variant="callout1"
          component="p"
          className={textClassName}
          sx={(theme) => ({ color: theme.palette.text.brandSecondary })}
        >
          {timeLabel}
        </Typography>
      )}
    </Box>
  );
};

export default GraphVerticalBar;
